import { useEffect, useMemo, useRef, useState } from "react";

/**
 * AreaPicker — choose a person's subject areas (ADR-0212 §4.5).
 *
 * The same chip interaction as `MemberPicker` — chips, type to filter, arrow
 * keys, Enter, Escape, Backspace removes the last chip, a click outside closes
 * — and the same `memberPicker*` classes, minus the kind badge. A sibling
 * rather than a generalised `MemberPicker`: that component's candidates are
 * the member directory's shape, whose `kind` its consumers read; taking areas
 * would widen it for every member picker (impl plan step 0.4).
 *
 * Multi-select only. Does NOT fetch — callers pass `candidates` (non-Master
 * areas, labelled) and save the whole set on every change.
 *
 * **Do not wrap this component in a native <label>** (see `MemberPicker`):
 * use a <div className="field"> with a <span className="label"> header.
 */

export interface AreaCandidate {
  id: string;
  label: string;
}

export interface AreaPickerProps {
  candidates: AreaCandidate[];
  selected: string[];
  onChange: (next: string[]) => void;
  placeholder?: string;
  disabled?: boolean;
}

export function AreaPicker({ candidates, selected, onChange, placeholder, disabled }: AreaPickerProps) {
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const [activeIdx, setActiveIdx] = useState(0);
  const wrapRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const byId = useMemo(() => new Map(candidates.map((c) => [c.id, c] as const)), [candidates]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    const chosen = new Set(selected);
    return candidates.filter((c) => !chosen.has(c.id) && (!q || c.label.toLowerCase().includes(q)));
  }, [candidates, selected, query]);

  useEffect(() => {
    setActiveIdx(0);
  }, [query, filtered.length]);

  // Close when clicking outside — pointerdown to beat the option's mousedown.
  useEffect(() => {
    if (!open) return;
    function handler(e: PointerEvent) {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener("pointerdown", handler);
    return () => document.removeEventListener("pointerdown", handler);
  }, [open]);

  function add(c: AreaCandidate) {
    onChange([...selected, c.id]);
    setQuery("");
    setOpen(true);
    inputRef.current?.focus();
  }

  function onKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setOpen(true);
      setActiveIdx((i) => Math.min(filtered.length - 1, i + 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIdx((i) => Math.max(0, i - 1));
    } else if (e.key === "Enter") {
      if (open && filtered[activeIdx]) {
        e.preventDefault();
        add(filtered[activeIdx]);
      }
    } else if (e.key === "Escape") {
      e.preventDefault();
      setOpen(false);
    } else if (e.key === "Backspace" && query === "" && selected.length > 0) {
      e.preventDefault();
      onChange(selected.slice(0, -1));
    }
  }

  const chips = selected.map((id) => byId.get(id)).filter((c): c is AreaCandidate => Boolean(c));

  return (
    <div className="memberPickerWrap" ref={wrapRef}>
      <div
        className="memberPickerChips"
        onClick={(e) => {
          if (e.target === e.currentTarget && !disabled) {
            inputRef.current?.focus();
            setOpen(true);
          }
        }}
      >
        {chips.map((c) => (
          <span key={c.id} className="pill memberPickerChip">
            <span className="memberPickerChipLabel">{c.label}</span>
            {!disabled && (
              <button
                type="button"
                className="memberPickerChipRemove"
                aria-label={`Remove ${c.label}`}
                onPointerDown={(e) => e.stopPropagation()}
                onClick={(e) => {
                  e.stopPropagation();
                  onChange(selected.filter((x) => x !== c.id));
                }}
              >
                ×
              </button>
            )}
          </span>
        ))}
        <input
          ref={inputRef}
          className="memberPickerInput"
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setOpen(true);
          }}
          onClick={(e) => {
            e.stopPropagation();
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          onKeyDown={onKeyDown}
          placeholder={chips.length === 0 ? placeholder : ""}
          disabled={disabled}
        />
      </div>
      {open && !disabled && filtered.length > 0 && (
        <div className="memberPickerDropdown" role="listbox">
          {filtered.map((c, idx) => (
            <div
              key={c.id}
              role="option"
              aria-selected={idx === activeIdx}
              className={`memberPickerOption${idx === activeIdx ? " isActive" : ""}`}
              onMouseDown={(e) => {
                e.preventDefault();
                add(c);
              }}
              onMouseEnter={() => setActiveIdx(idx)}
            >
              <span style={{ flex: 1 }}>{c.label}</span>
            </div>
          ))}
        </div>
      )}
      {open && !disabled && filtered.length === 0 && (
        <div className="memberPickerDropdown memberPickerDropdownEmpty">
          <div className="muted" style={{ padding: "6px 8px", fontSize: 12 }}>
            {query ? "No matches." : "No subject areas to add."}
          </div>
        </div>
      )}
    </div>
  );
}
